/**
 * PrivacyRequestsSection — iter85 (CCPA / CAN-SPAM pipeline, Platform scope)
 *
 * Queue of privacy requests (submitted by tenant admins or directly here),
 * with impact preview, typed-confirmation execute, and receipts.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldAlert, Loader2, ChevronDown, ChevronRight, Search, Play,
    XCircle, Download, Plus, RotateCcw, Ban,
} from "lucide-react";

const STATUS_COLOR = {
    pending: "text-amber-400 border-amber-400/40",
    completed: "text-emerald-400 border-emerald-400/40",
    cancelled: "text-zinc-500 border-[#333]",
    rejected: "text-red-400 border-red-400/40",
    restored: "text-cyan-400 border-cyan-400/40",
};

// iter87.71 — undo-window countdown helper. `restorable_until` is
// an ISO string; returns "6d 23h" style copy, or null when the
// window is null or has passed.
function formatUndoRemaining(iso) {
    if (!iso) return null;
    const deadline = new Date(iso).getTime();
    const now = Date.now();
    const ms = deadline - now;
    if (ms <= 0) return null;
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
}

export default function PrivacyRequestsSection() {
    const [open, setOpen] = useState(true);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ email: "", request_type: "anonymize", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.privacyRequestsList();
            setRows(r.rows);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load privacy requests");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async () => {
        const email = form.email.trim().toLowerCase();
        if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
        setSubmitting(true);
        try {
            if (form.request_type === "suppress_only") {
                // iter87.71 — separate endpoint, no queue; instant do-not-contact.
                await api.privacySuppressOnly(email, form.notes || null);
                toast.success(`${email} added to suppression list`);
            } else {
                await api.privacyRequestSubmit({ email, request_type: form.request_type, notes: form.notes });
                toast.success("Privacy request queued");
            }
            setForm({ email: "", request_type: "anonymize", notes: "" });
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to submit request");
        }
        setSubmitting(false);
    };

    const pendingCount = rows.filter((r) => r.status === "pending").length;

    return (
        <section className="brutal-card" data-testid="privacy-requests-section">
            <button
                type="button"
                data-testid="privacy-requests-toggle"
                onClick={() => setOpen((v) => !v)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-[#0a0a0a]/40 transition-colors"
            >
                {open ? <ChevronDown className="w-4 h-4 text-cyber shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-500 shrink-0" />}
                <ShieldAlert className="w-5 h-5 text-cyber shrink-0" />
                <div className="flex-1">
                    <h3 className="font-display font-extrabold text-base uppercase">Privacy Requests (CCPA / CAN-SPAM)</h3>
                    <p className="text-[11px] text-zinc-500">
                        Anonymize identity, preserve game data. Executed emails land on the suppression list forever.
                    </p>
                </div>
                {pendingCount > 0 && (
                    <span className="pill text-[10px] font-bold uppercase border-amber-400/40 text-amber-400" data-testid="privacy-pending-pill">
                        {pendingCount} pending
                    </span>
                )}
            </button>

            {open && (
                <div className="p-4 pt-0 space-y-4">
                    <div className="brutal-card-light p-3 space-y-2" data-testid="privacy-submit-form">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Queue a new request</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <input
                                data-testid="privacy-form-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="user@example.com"
                                className="brutal-input text-xs"
                            />
                            <select
                                data-testid="privacy-form-type"
                                value={form.request_type}
                                onChange={(e) => setForm({ ...form, request_type: e.target.value })}
                                className="brutal-input text-xs"
                            >
                                <option value="anonymize">Anonymize (keep game data)</option>
                                <option value="hard_delete">Hard delete (+ remove messages)</option>
                                <option value="suppress_only">Suppress only (do-not-contact, no data touched)</option>
                            </select>
                            <input
                                data-testid="privacy-form-notes"
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="Notes (request source, ticket #…)"
                                className="brutal-input text-xs"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={submitting}
                            data-testid="privacy-form-submit"
                            className="brutal-btn-primary text-xs py-2 disabled:opacity-60"
                        >
                            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                            {submitting ? "Queuing…" : "Queue request"}
                        </button>
                    </div>

                    {loading ? (
                        <div className="flex items-center justify-center gap-2 p-6 text-zinc-400 text-xs">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="text-xs text-zinc-500 italic text-center p-4" data-testid="privacy-empty">
                            No privacy requests in the queue.
                        </p>
                    ) : (
                        <div className="space-y-2" data-testid="privacy-requests-list">
                            {rows.map((row) => (
                                <RequestRow key={row.id} row={row} onChanged={load} />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

function RequestRow({ row, onChanged }) {
    const [impact, setImpact] = useState(null);
    const [impactBusy, setImpactBusy] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const [executing, setExecuting] = useState(false);
    // iter87.71 — reject reason modal state
    const [showReject, setShowReject] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejecting, setRejecting] = useState(false);
    // iter87.71 — restore state (undo within 7 days)
    const [restoring, setRestoring] = useState(false);
    // Live-refresh undo countdown once a minute (rendered from
    // row.restorable_until — parent still owns the row list).
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        if (row.status !== "completed" || !row.restorable_until) return undefined;
        const id = setInterval(() => setNowTick(Date.now()), 60000);
        return () => clearInterval(id);
    }, [row.status, row.restorable_until]);
    void nowTick; // keep re-render on tick

    const loadImpact = async () => {
        setImpactBusy(true);
        try {
            const r = await api.privacyImpact(row.email);
            setImpact(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Impact preview failed");
        }
        setImpactBusy(false);
    };

    const execute = async () => {
        setExecuting(true);
        try {
            const r = await api.privacyRequestExecute(row.id, confirmText.trim());
            const undoMsg = r.restorable_until ? " · restore available for 7 days" : "";
            toast.success(`Executed — ${r.receipt.total_touched} documents touched${undoMsg}`);
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Execution failed");
        }
        setExecuting(false);
    };

    const cancel = async () => {
        try {
            await api.privacyRequestCancel(row.id);
            toast.success("Request cancelled");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        }
    };

    // iter87.71 — reject: platform admin declines a pending request with a reason.
    const reject = async () => {
        const reason = rejectReason.trim();
        if (reason.length < 3) {
            toast.error("Rejection reason must be at least 3 characters");
            return;
        }
        setRejecting(true);
        try {
            await api.privacyRequestReject(row.id, reason);
            toast.success("Request rejected");
            setShowReject(false);
            setRejectReason("");
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        }
        setRejecting(false);
    };

    // iter87.71 — restore: reverse an executed anonymization within the undo window.
    const restore = async () => {
        setRestoring(true);
        try {
            const r = await api.privacyRequestRestore(row.id);
            toast.success(`Restored — ${r.report?.participants?.modified ?? 0} participant row(s) recovered`);
            onChanged();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Restore failed");
        }
        setRestoring(false);
    };

    const downloadReceipt = () => {
        const blob = new Blob([JSON.stringify(row.receipt, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `privacy-receipt-${row.id.slice(0, 8)}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const undoRemaining = row.status === "completed"
        ? formatUndoRemaining(row.restorable_until)
        : null;

    return (
        <div className="brutal-card-light p-3 space-y-2" data-testid={`privacy-request-${row.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className={`pill text-[10px] font-bold uppercase ${STATUS_COLOR[row.status] || "text-zinc-400 border-[#333]"}`} data-testid="privacy-request-status">
                    {row.status}
                </span>
                <span className="font-mono text-xs text-white">{row.email}</span>
                <span className="pill text-[10px] uppercase border-[#333] text-zinc-400">{row.request_type}</span>
                <span className="text-[10px] text-zinc-500 ml-auto">
                    by {row.submitted_by} · {(row.created_at || "").slice(0, 16).replace("T", " ")}
                </span>
            </div>
            {row.notes && <p className="text-[11px] text-zinc-400">{row.notes}</p>}
            {row.rejection_reason && (
                <p className="text-[11px] text-red-400" data-testid="privacy-rejection-reason">
                    Rejected: {row.rejection_reason}
                </p>
            )}

            {row.status === "pending" && (
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={loadImpact} disabled={impactBusy} data-testid="privacy-impact-btn" className="brutal-btn-ghost text-xs py-1.5">
                        {impactBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Impact preview
                    </button>
                    <button type="button" onClick={() => setShowConfirm((v) => !v)} data-testid="privacy-execute-toggle" className="brutal-btn-primary text-xs py-1.5">
                        <Play className="w-3 h-3" /> Execute…
                    </button>
                    <button type="button" onClick={() => setShowReject((v) => !v)} data-testid="privacy-reject-toggle" className="brutal-btn-ghost text-xs py-1.5 text-red-400">
                        <Ban className="w-3 h-3" /> Reject…
                    </button>
                    <button type="button" onClick={cancel} data-testid="privacy-cancel-btn" className="brutal-btn-ghost text-xs py-1.5 text-zinc-400">
                        <XCircle className="w-3 h-3" /> Cancel
                    </button>
                </div>
            )}

            {impact && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="privacy-impact-grid">
                    {impact.plan.filter((p) => p.matched > 0).length === 0 ? (
                        <p className="col-span-full text-[11px] text-zinc-500 italic">No documents reference this email.</p>
                    ) : impact.plan.filter((p) => p.matched > 0).map((p) => (
                        <div key={p.collection} className="p-2 border border-cyber/40 bg-cyber/5" data-testid={`privacy-impact-${p.collection}`}>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{p.collection}</div>
                            <div className="text-sm font-display font-extrabold text-cyber">{p.matched} <span className="text-[9px] text-zinc-500 uppercase">{p.action}</span></div>
                        </div>
                    ))}
                </div>
            )}

            {showConfirm && row.status === "pending" && (
                <div className="flex gap-2 items-stretch border-t border-[#222] pt-2" data-testid="privacy-confirm-row">
                    <input
                        data-testid="privacy-confirm-input"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={`Type "${row.email}" to confirm`}
                        className="brutal-input text-xs flex-1 font-mono"
                    />
                    <button
                        type="button"
                        onClick={execute}
                        disabled={executing || confirmText.trim().toLowerCase() !== row.email}
                        data-testid="privacy-confirm-execute"
                        className="brutal-btn-primary text-xs py-2 disabled:opacity-40"
                    >
                        {executing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        {executing ? "Executing…" : "Execute now"}
                    </button>
                </div>
            )}

            {showReject && row.status === "pending" && (
                <div className="flex gap-2 items-stretch border-t border-[#222] pt-2" data-testid="privacy-reject-row">
                    <input
                        data-testid="privacy-reject-reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Rejection reason (visible in audit log)"
                        className="brutal-input text-xs flex-1"
                        maxLength={1000}
                    />
                    <button
                        type="button"
                        onClick={reject}
                        disabled={rejecting || rejectReason.trim().length < 3}
                        data-testid="privacy-reject-submit"
                        className="brutal-btn-ghost text-xs py-2 text-red-400 disabled:opacity-40"
                    >
                        {rejecting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                        {rejecting ? "Rejecting…" : "Confirm reject"}
                    </button>
                </div>
            )}

            {row.status === "completed" && row.receipt && (
                <div className="border-t border-[#222] pt-2 space-y-1.5" data-testid="privacy-receipt">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-[11px] text-zinc-400">
                            Executed by <strong className="text-white">{row.executed_by}</strong> —{" "}
                            <span className="font-mono text-cyber">{row.receipt.anon_token}</span> ·{" "}
                            {row.receipt.total_touched} docs touched
                        </p>
                        <div className="flex items-center gap-2">
                            {undoRemaining ? (
                                <span
                                    className="pill text-[10px] font-bold uppercase border-cyan-400/40 text-cyan-400"
                                    data-testid="privacy-undo-countdown"
                                    title="Time left in the 7-day restore window"
                                >
                                    Undo: {undoRemaining} left
                                </span>
                            ) : (
                                <span
                                    className="pill text-[10px] uppercase border-[#333] text-zinc-500"
                                    data-testid="privacy-undo-permanent"
                                    title="Undo window has expired"
                                >
                                    Permanent
                                </span>
                            )}
                            {undoRemaining && (
                                <button
                                    type="button"
                                    onClick={restore}
                                    disabled={restoring}
                                    data-testid="privacy-restore-btn"
                                    className="brutal-btn-ghost text-xs py-1 text-cyan-400 disabled:opacity-40"
                                >
                                    {restoring ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                                    Restore
                                </button>
                            )}
                            <button type="button" onClick={downloadReceipt} data-testid="privacy-receipt-download" className="brutal-btn-ghost text-xs py-1">
                                <Download className="w-3 h-3" /> Receipt
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {Object.entries(row.receipt.collections).filter(([, v]) => v.modified > 0).map(([coll, v]) => (
                            <span key={coll} className="pill text-[9px] uppercase border-[#333] text-zinc-400">
                                {coll}: {v.modified} {v.action === "delete" ? "deleted" : "anonymized"}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {row.status === "restored" && (
                <div className="border-t border-[#222] pt-2 space-y-1" data-testid="privacy-restored-info">
                    <p className="text-[11px] text-cyan-400">
                        Restored by <strong className="text-white">{row.restored_by}</strong>
                        {row.restored_at ? ` on ${row.restored_at.slice(0, 16).replace("T", " ")}` : ""}
                    </p>
                </div>
            )}
        </div>
    );
}
